import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faCheckCircle, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import { AuroraBackground } from '../ui/aurora-background';
import { businessAuthAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

interface SocialMediaDataSyncProps {
  onComplete: () => void;
}

const SocialMediaDataSync: React.FC<SocialMediaDataSyncProps> = ({ onComplete }) => {
  const { refreshBusinessProfile } = useAuth();
  const [status, setStatus] = useState<'syncing' | 'success' | 'error'>('syncing');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const syncData = async () => {
      try {
        // Sync işlemini başlat
        await businessAuthAPI.syncSocialMediaData();
        
        // İşlem başarıyla başlatıldı, başarı durumunu göster
        setStatus('success');
        
        // Business profile'ı yenile
        await refreshBusinessProfile();
        
        // 2 saniye sonra tamamlandı olarak işaretle ve yönlendir
        setTimeout(() => {
          onComplete();
        }, 2000);
      } catch (err: any) {
        console.error('Sync error:', err);
        setError(err.response?.data?.message || 'Veriler çekilirken bir hata oluştu');
        setStatus('error');
      }
    };

    syncData();
  }, [onComplete, refreshBusinessProfile]);

  return (
    <AuroraBackground className="w-full h-full overflow-hidden">
      <div className="w-full min-h-screen flex items-center justify-center relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-purple-900/20 to-gray-900"></div>
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>

        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-purple-500/10 rounded-full blur-xl animate-float"></div>
        <div className="absolute top-40 right-20 w-32 h-32 bg-blue-500/10 rounded-full blur-xl animate-float" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-20 left-1/4 w-24 h-24 bg-pink-500/10 rounded-full blur-xl animate-float" style={{ animationDelay: '2s' }}></div>

        <div className="relative z-10 w-full max-w-md mx-auto px-6">
          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-3xl p-12 shadow-2xl text-center">
            {status === 'syncing' && (
              <>
                <div className="w-20 h-20 bg-gradient-to-br from-purple-500/20 to-purple-700/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <FontAwesomeIcon icon={faSpinner} className="text-purple-400 text-4xl animate-spin" />
                </div>
                <h1 className="text-3xl font-bold text-white mb-4">
                  Verileriniz Çekiliyor
                </h1>
                <p className="text-white/70 mb-6">
                  Sosyal medya verileriniz veritabanına kaydediliyor. Lütfen bekleyin...
                </p>
                <div className="flex justify-center space-x-2">
                  <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
                  <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                </div>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="w-20 h-20 bg-gradient-to-br from-green-500/20 to-green-700/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-green-400 text-4xl" />
                </div>
                <h1 className="text-3xl font-bold text-white mb-4">
                  Başarılı!
                </h1>
                <p className="text-white/70">
                  Verileriniz başarıyla çekildi. Yönlendiriliyorsunuz...
                </p>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="w-20 h-20 bg-gradient-to-br from-red-500/20 to-red-700/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <FontAwesomeIcon icon={faExclamationCircle} className="text-red-400 text-4xl" />
                </div>
                <h1 className="text-3xl font-bold text-white mb-4">
                  Hata Oluştu
                </h1>
                <p className="text-white/70 mb-6">
                  {error || 'Veriler çekilirken bir hata oluştu'}
                </p>
                <button
                  onClick={() => {
                    setStatus('syncing');
                    setError(null);
                    // Retry sync
                    const syncData = async () => {
                      try {
                        await businessAuthAPI.syncSocialMediaData();
                        setStatus('success');
                        await refreshBusinessProfile();
                        setTimeout(() => {
                          onComplete();
                        }, 2000);
                      } catch (err: any) {
                        setError(err.response?.data?.message || 'Veriler çekilirken bir hata oluştu');
                        setStatus('error');
                      }
                    };
                    syncData();
                  }}
                  className="px-6 py-3 bg-purple-500 hover:bg-purple-600 rounded-xl text-white font-semibold transition-all duration-300"
                >
                  Tekrar Dene
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </AuroraBackground>
  );
};

export default SocialMediaDataSync;

